/**
 * Single definition of the "open the create-note editor for a player" deep link.
 *
 * Notes are player-scoped by schema (`CreateNoteInput.playerId` is required), so
 * every create-note entry point must resolve a player first and then hand off to
 * the player-detail screen. That handoff is a URL contract, honoured by three
 * hops that already exist:
 *
 *   1. `player-detail/screens/Main/MainContainer` reads `?tab=notes&action=create`
 *      and sets `selectPlayerTab(playerId, "reportsNotes")` +
 *      `playerAutoCreateNoteVar(true)`.
 *   2. `player-detail/.../NotesSection/NotesSectionContainer` reads that var in its
 *      state initializer and opens the editor in `{ mode: "add" }`.
 *   3. The editor saves through the existing `NotesCreateNote` mutation.
 *
 * The query string was previously spelled inline in `useAddMenuActions`. It now
 * lives here so a second entry point (the Home quick action, SE-6845) cannot
 * drift from the reader in `MainContainer` — if the contract changes, it changes
 * in one place.
 * @module hooks/useOpenCreateNoteForPlayer
 */

import { Href, useRouter } from "expo-router";
import { useCallback } from "react";

import { useGetExpandedPlayerUrl } from "@/hooks/useExpandedPlayerUrl";

/**
 * Builds the create-note deep link for a player.
 *
 * Exported for tests and for callers that need the path without navigating.
 * @param playerId - The player the note will be attached to
 * @returns The player-detail path that auto-opens the note editor
 */
export const buildCreateNoteHref = (playerId: string): string =>
  `/players/${playerId}?tab=notes&action=create`;

/**
 * Returns a callback that navigates to a player's detail screen with the note
 * editor already open in "add" mode.
 * @returns A stable `(playerId: string) => void` navigation callback
 */
const useOpenCreateNoteForPlayer = () => {
  const router = useRouter();
  const getPlayerUrl = useGetExpandedPlayerUrl();

  return useCallback(
    (playerId: string) => {
      router.push(getPlayerUrl(buildCreateNoteHref(playerId)) as Href);
    },
    [getPlayerUrl, router]
  );
};

export default useOpenCreateNoteForPlayer;
